"use client";

import { useEffect, useRef } from "react";
import { AnimatedGroup, AnimatedText } from "@/components/smoothui/shared";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, CirclePlay } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import styles from "./hero-grid.module.css";
import { Badge } from "@/components/ui/badge";
import { getAuthUrl } from "@/lib/auth-urls";
import { useLanguage } from "@/lib/LanguageContext";

const TOTAL_TILES = 1600;
const INITIAL_TILE = 1;
const TILES_TO_CLONE = TOTAL_TILES - INITIAL_TILE;

function InteractiveGrid() {
    const containerRef = useRef<HTMLDivElement>(null);
    const tileRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!(containerRef.current && tileRef.current)) {
            return;
        }

        // Clone the first tile to create a 40x40 grid (1600 tiles total)
        // Only if not already populated
        if (containerRef.current.children.length <= 1) {
            for (let i = 0; i < TILES_TO_CLONE; i++) {
                const clonedTile = tileRef.current.cloneNode(true);
                containerRef.current.appendChild(clonedTile);
            }
        }
    }, []);

    return (
        <div
            aria-hidden="true"
            className={styles.gridContainer}
        >
            <div className={styles.mainGrid} ref={containerRef}>
                <div className={styles.tile} ref={tileRef} />
            </div>
        </div>
    );
}

const Hero = () => {
    const t = useTranslations('marketing.hero');
    const { language } = useLanguage();

    return (
        <div className="relative border-b border-border/40 bg-background">
            <main>
                <section className="relative overflow-hidden py-24 sm:py-32 lg:py-40">
                    {/* Interactive animated grid background */}
                    <InteractiveGrid />

                    <AnimatedGroup
                        className="pointer-events-none flex flex-col items-center gap-8 text-center relative z-10 px-4"
                        preset="blur-slide"
                    >
                        <Badge variant="outline" className="rounded-full py-1.5 px-4 text-sm font-medium backdrop-blur-sm bg-background/50 border-primary/20 text-primary animate-fade-in">
                            {t('badge')}
                        </Badge>

                        <div className="max-w-4xl mx-auto space-y-4">
                            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-foreground">
                                <AnimatedText as="span" className="block text-primary">
                                    {t('headline.build')} {t('headline.scale')}
                                </AnimatedText>
                                <AnimatedText as="span" delay={0.1}>
                                    {t('headline.dominate')}
                                </AnimatedText>
                            </h1>

                            <AnimatedText
                                as="p"
                                className="mx-auto max-w-2xl text-lg sm:text-xl text-muted-foreground leading-relaxed"
                                delay={0.2}
                            >
                                {t('description')}
                            </AnimatedText>
                        </div>

                        <AnimatedGroup
                            className="pointer-events-auto mt-4 flex flex-col sm:flex-row items-center justify-center gap-4"
                            preset="slide"
                        >
                            <Link href={getAuthUrl(language, '/signup')}>
                                <Button size="lg" className="rounded-full text-base px-8 h-12 shadow-lg shadow-primary/20">
                                    {t('cta.startFree')} <ArrowUpRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <Link href="#features">
                                <Button variant="outline" size="lg" className="rounded-full text-base px-8 h-12 backdrop-blur-sm bg-background/50">
                                    {t('cta.explore')} <CirclePlay className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                        </AnimatedGroup>
                    </AnimatedGroup>
                </section>
            </main>
        </div>
    );
};

export default Hero;
